import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';
import { SpinnerComponent } from '../feedback/spinner.component';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg' | 'field';

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed';

const SIZES: Record<ButtonSize, string> = {
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
  field: 'h-14 px-6 text-base',
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-bsite-400 text-ink hover:bg-bsite-300',
  secondary:
    'border border-stone-300 bg-white text-ink hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-50 dark:hover:bg-stone-800',
  ghost:
    'text-stone-600 hover:bg-stone-100 hover:text-ink dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50',
  danger: 'bg-red-700 text-white hover:bg-red-800',
};

/**
 * Pulsante della libreria. Con `link` diventa un collegamento del router con lo stesso aspetto,
 * per le azioni che portano a una pagina (per esempio l'azione principale della barra laterale).
 */
@Component({
  selector: 'bs-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, IconComponent, SpinnerComponent],
  host: { '[class]': 'hostClass()' },
  template: `
    <ng-template #content>
      @if (loading()) {
        <bs-spinner [size]="18" />
      } @else if (icon(); as name) {
        <bs-icon [name]="name" [size]="18" />
      }
      <span class="truncate"><ng-content /></span>
    </ng-template>
    @if (link(); as target) {
      <a
        #control
        [routerLink]="target"
        [fragment]="fragment() ?? undefined"
        [queryParams]="queryParams() ?? undefined"
        [class]="classes()"
        [attr.data-testid]="testId()"
        [attr.aria-label]="ariaLabel()"
      >
        <ng-container [ngTemplateOutlet]="content" />
      </a>
    } @else {
      <button
        #control
        [type]="type()"
        [class]="classes()"
        [disabled]="disabled() || loading()"
        [attr.aria-busy]="loading() ? 'true' : null"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-expanded]="expanded()"
        [attr.aria-controls]="controls()"
        [attr.data-testid]="testId()"
      >
        <ng-container [ngTemplateOutlet]="content" />
      </button>
    }
  `,
})
export class ButtonComponent {
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('md');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly icon = input<IconName | null>(null);
  readonly loading = input(false);
  readonly disabled = input(false);
  readonly fullWidth = input(false);
  readonly link = input<string | readonly unknown[] | null>(null);
  /** Ancora della pagina di destinazione, per esempio `login-email`. */
  readonly fragment = input<string | null>(null);
  /** Parametri dell'indirizzo di destinazione, per esempio `{ returnUrl: '/invitations' }`. */
  readonly queryParams = input<Record<string, string> | null>(null);
  readonly ariaLabel = input<string | null>(null);
  readonly expanded = input<boolean | null>(null);
  readonly controls = input<string | null>(null);
  readonly testId = input<string | null>(null);

  private readonly control = viewChild<ElementRef<HTMLElement>>('control');

  protected readonly hostClass = computed(() => (this.fullWidth() ? 'flex w-full' : 'inline-flex'));
  protected readonly classes = computed(
    () =>
      `${BASE} ${SIZES[this.size()]} ${VARIANTS[this.variant()]} ${this.fullWidth() ? 'w-full' : ''}`,
  );

  focus(): void {
    this.control()?.nativeElement.focus();
  }
}
